import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStopwatch } from "react-timer-hook";
import {
  FlagCountRequest,
  FlagCountResponse,
  GroupCount,
  ItemTypes,
  ItemWithMetaData,
  ItemsForReviewResponse,
  KeywordsCountRequest,
  TopicCount,
  TopicCountRequest,
} from "../api/Items";
import {
  useFetchFlagCount,
  useFetchItemsForReview,
  useFetchItemsForSummary,
  useFetchKeywordsCount,
  useFetchTopicsCount,
} from "../api/ItemsApi";
import { useReportByUUID } from "../api/ReportsApi";
import { ReportSummaryItemApi } from "../api/Reports";
import { useLocation, useParams } from "react-router-dom";
import { cacheUpdateEvents } from "../services/websocket/cacheUpdateEvents";
import { useGetComputedBlueprintFromWorkflow } from "../api/BlueprintsApi";
import { BlueprintEx2 } from "../api/Blueprints";
import { usePermissionValidator } from "../api/UserPermissionValidators";

// Large enough to return every flagged profile for a typical report in one page;
// the scroll loader below handles the rare report that exceeds it.
const PROFILE_CONFIRM_PAGE_SIZE = 100;

const ReportDetailContext = createContext<ReportDetailProviderType>(null);

type ReportDetailProviderType = {
  seconds: number;
  minutes: number;
  startTimer: () => void;
  resetTimer: () => void;
  pauseTimer: () => void;
  isRunningTimer: boolean;
  loadProfiles: () => void;
  loadProfilesConfirm: () => void | Promise<void>;
  loadMoreProfilesConfirm: () => void;
  hasMoreProfilesConfirm: boolean;
  isLoadingMoreProfilesConfirm: boolean;
  profileList: ItemWithMetaData[] | undefined;
  isLoadingProfiles: boolean;
  isSuccessProfiles: boolean;
  profileListConfirm: ItemsForReviewResponse | undefined;
  isLoadingProfilesConfirm: boolean;
  isSuccessProfilesConfirm: boolean;
  report: ReportSummaryItemApi | undefined;
  errorLoadingReport: boolean;
  isReportForbidden: boolean;
  isSuccessLoadingReport: boolean;
  errorLoadingProfiles: boolean;
  errorLoadingProfilesConfirm: boolean;
  blueprint: BlueprintEx2 | undefined;
  isSuccessLoadingBlueprintAnnotated: boolean;
  errorBlueprint: boolean;
  isLoadingBlueprint: boolean;
  flagCount: FlagCountResponse | undefined;
  keywordCount: GroupCount | undefined;
  topicCount: TopicCount | undefined;
  getFlags: (
    params: FlagCountRequest,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  getKeywordCount: (
    params: KeywordsCountRequest,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  getTopicCount: (
    params: TopicCountRequest,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  flagsLoading: boolean;
  keywordsLoading: boolean;
  topicsLoading: boolean;
  errorFlag: boolean;
  errorKeyword: boolean;
  errorTopic: boolean;
  filteredCountsReady: boolean;
  setFilteredCountsReady: (ready: boolean) => void;
} | null;
interface LocationState {
  report: ReportSummaryItemApi;
  isReadOnly: boolean;
}

export const ReportDetailProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const { id } = useParams();
  const location = useLocation();
  const { canViewAllReportsWithinCompany } = usePermissionValidator();

  const [filteredCountsReady, setFilteredCountsReady] = useState(true);
  const {
    data: flagCount,
    mutate: getFlags,
    error: errorFlag,
    isLoading: flagsLoading,
  } = useFetchFlagCount();
  const {
    data: keywordCount,
    mutate: getKeywordCount,
    error: errorKeyword,
    isLoading: keywordsLoading,
  } = useFetchKeywordsCount();
  const {
    data: topicCount,
    mutate: getTopicCount,
    error: errorTopic,
    isLoading: topicsLoading,
  } = useFetchTopicsCount();
  const locationState = (location.state as LocationState) ?? {
    report: null,
    isReadOnly: true,
  };
  const [report, setReport] = useState<ReportSummaryItemApi | undefined>();
  const {
    seconds,
    minutes,
    start: startTimer,
    reset: resetTimer,
    pause: pauseTimer,
    isRunning: isRunningTimer,
  } = useStopwatch({
    autoStart: false,
  });
  const {
    mutateAsync: fetchProfilesConfirmPage,
    error: errorLoadingProfilesConfirm,
  } = useFetchItemsForReview();
  const [profileListConfirm, setProfileListConfirm] = useState<
    ItemsForReviewResponse | undefined
  >();
  const [confirmPage, setConfirmPage] = useState(1);
  const [confirmReachedEnd, setConfirmReachedEnd] = useState(false);
  const [isLoadingProfilesConfirm, setIsLoadingProfilesConfirm] =
    useState(false);
  const [isSuccessProfilesConfirm, setIsSuccessProfilesConfirm] =
    useState(false);
  const [isLoadingMoreProfilesConfirm, setIsLoadingMoreProfilesConfirm] =
    useState(false);
  // Tracks the initial/refresh page-1 fetch synchronously so a scroll-triggered
  // load-more can't race it (and merge a page fetched with a stale page index into
  // the previous report's cached list). A ref, not state, so the guard is correct
  // within the same tick the initial load starts, before any re-render.
  const loadProfilesConfirmInFlightRef = useRef(false);
  // Serializes load-more so two rapid scroll events can't both pass the (state) loading
  // guard within the same tick, fetch the same page, and have the second run see zero
  // fresh rows and wrongly mark the list ended while later pages still exist.
  const loadMoreProfilesConfirmInFlightRef = useRef(false);

  const {
    isLoading: isLoadingProfiles,
    data: profileList,
    mutate: getProfiles,
    isSuccess: isSuccessProfiles,
    error: errorLoadingProfiles,
  } = useFetchItemsForSummary();

  const {
    data: blueprint,
    mutate: getBlueprint,
    error: errorBlueprint,
    isSuccess: isSuccessLoadingBlueprintAnnotated,
    isLoading: isLoadingBlueprint,
  } = useGetComputedBlueprintFromWorkflow();

  const {
    data: queriedReport,
    mutate: getReport,
    error: errorLoadingReport,
    isSuccess: isSuccessLoadingReport,
  } = useReportByUUID(canViewAllReportsWithinCompany);

  useEffect(() => {
    if (locationState.report) {
      setReport(locationState.report);
      if (locationState.isReadOnly && !locationState.report.is_locked && id) {
        getReport(id);
      }
    } else if (id) {
      getReport(id);
    }
  }, [locationState.report, locationState.isReadOnly, getReport, id]);

  useEffect(() => {
    if (isSuccessLoadingReport) {
      setReport(queriedReport);
    }
  }, [isSuccessLoadingReport, queriedReport]);

  useEffect(() => {
    if (!id) return;
    const unsubscribe = cacheUpdateEvents.subscribe((affectedReportUuids) => {
      if (affectedReportUuids.includes(id)) {
        getReport(id);
      }
    });
    return unsubscribe;
  }, [id, getReport]);

  const loadProfiles = useCallback(() => {
    if (!report?.report_uuid) return;
    getProfiles({
      report_id: report.report_uuid,
      item_types: [ItemTypes.SOCIAL_MEDIA_PROFILE],
      page_number: 1,
      page_size: 50,
    });
  }, [getProfiles, report?.report_uuid]);

  const loadProfilesConfirm = useCallback(async () => {
    if (!report?.report_uuid) return;
    // Reset pagination synchronously at the start so a concurrent scroll can't fetch
    // the "next" page off a stale index (e.g. left over from the previous report).
    loadProfilesConfirmInFlightRef.current = true;
    setConfirmPage(1);
    setConfirmReachedEnd(false);
    setIsLoadingProfilesConfirm(true);
    setIsSuccessProfilesConfirm(false);
    try {
      const res = await fetchProfilesConfirmPage({
        report_uuid: report.report_uuid,
        item_type_name: [ItemTypes.SOCIAL_MEDIA_PROFILE],
        // EI /item/items_for_review uses page_num (page is ignored).
        page_num: 1,
        page_size: PROFILE_CONFIRM_PAGE_SIZE,
      });
      setProfileListConfirm(res);
      setConfirmPage(1);
      setConfirmReachedEnd(false);
      setIsSuccessProfilesConfirm(true);
    } catch {
      // Error is surfaced through errorLoadingProfilesConfirm.
    } finally {
      loadProfilesConfirmInFlightRef.current = false;
      setIsLoadingProfilesConfirm(false);
    }
  }, [fetchProfilesConfirmPage, report?.report_uuid]);

  const hasMoreProfilesConfirm =
    !confirmReachedEnd &&
    (profileListConfirm?.items?.length ?? 0) < (profileListConfirm?.total ?? 0);

  // "Load on scroll" safety net: page_size already covers typical reports in a
  // single page, but if a report exceeds it we fetch the next page and append,
  // de-duplicating by item id so a repeated page can never introduce dupes.
  const loadMoreProfilesConfirm = useCallback(async () => {
    if (!report?.report_uuid) return;
    // Never page while the initial/refresh load is in flight: confirmPage and the
    // cached list belong to the request that's about to be replaced.
    if (loadProfilesConfirmInFlightRef.current) return;
    // Synchronous guard: state updates below don't re-render before a second overlapping
    // scroll call would re-enter, so the ref is what actually prevents a duplicate fetch.
    if (loadMoreProfilesConfirmInFlightRef.current) return;
    if (isLoadingMoreProfilesConfirm || !hasMoreProfilesConfirm) return;
    loadMoreProfilesConfirmInFlightRef.current = true;
    setIsLoadingMoreProfilesConfirm(true);
    try {
      const nextPage = confirmPage + 1;
      const res = await fetchProfilesConfirmPage({
        report_uuid: report.report_uuid,
        item_type_name: [ItemTypes.SOCIAL_MEDIA_PROFILE],
        page_num: nextPage,
        page_size: PROFILE_CONFIRM_PAGE_SIZE,
      });
      setConfirmPage(nextPage);
      setProfileListConfirm((prev) => {
        if (!prev) return res;
        const seen = new Set(prev.items.map((i) => i.item.external_id));
        const fresh = res.items.filter((i) => !seen.has(i.item.external_id));
        // No new rows means the server has nothing more for us (or returned a
        // repeated page): stop so scrolling can't loop fetching forever.
        if (fresh.length === 0) setConfirmReachedEnd(true);
        return { ...res, items: [...prev.items, ...fresh] };
      });
    } catch {
      // Keep already-loaded items on failure; the user can scroll to retry.
    } finally {
      loadMoreProfilesConfirmInFlightRef.current = false;
      setIsLoadingMoreProfilesConfirm(false);
    }
  }, [
    confirmPage,
    fetchProfilesConfirmPage,
    hasMoreProfilesConfirm,
    isLoadingMoreProfilesConfirm,
    report?.report_uuid,
  ]);

  const loadBlueprints = useCallback(() => {
    if (!report?.report_uuid) return;
    getBlueprint({
      blueprintUUID: report.blueprint_uuid,
      companyUUID: report.company_uuid,
      behaviorKitUUID: report?.behavior_kit_uuid ?? "None",
      workflowRunUUID: report?.workflow_run_uuid,
    });
  }, [getBlueprint, report]);

  useEffect(() => {
    if (
      location.pathname === `/reports/${report?.report_uuid}/detail` ||
      location.pathname === `/reports/${report?.report_uuid}/amendment/detail`
    ) {
      loadProfilesConfirm();
    } else {
      loadProfiles();
    }
  }, [loadProfiles, loadProfilesConfirm, location.pathname, report]);

  useEffect(() => {
    loadBlueprints();
  }, [loadBlueprints]);

  return (
    <ReportDetailContext.Provider
      value={{
        startTimer,
        resetTimer,
        pauseTimer,
        isRunningTimer,
        seconds,
        minutes,
        loadProfiles,
        profileList,
        isLoadingProfiles,
        isSuccessProfiles,
        report,
        errorLoadingReport: !!errorLoadingReport,
        isReportForbidden:
          (errorLoadingReport as any)?.response?.status === 403,
        isSuccessLoadingReport,
        errorLoadingProfiles: !!errorLoadingProfiles,
        blueprint,
        isSuccessLoadingBlueprintAnnotated,
        errorBlueprint: !!errorBlueprint,
        isLoadingBlueprint,
        isLoadingProfilesConfirm,
        errorLoadingProfilesConfirm: !!errorLoadingProfilesConfirm,
        profileListConfirm,
        isSuccessProfilesConfirm,
        loadProfilesConfirm,
        loadMoreProfilesConfirm,
        hasMoreProfilesConfirm,
        isLoadingMoreProfilesConfirm,
        flagCount,
        keywordCount,
        topicCount,
        getFlags,
        getKeywordCount,
        getTopicCount,
        flagsLoading,
        keywordsLoading,
        topicsLoading,
        errorFlag: !!errorFlag,
        errorKeyword: !!errorKeyword,
        errorTopic: !!errorTopic,
        filteredCountsReady,
        setFilteredCountsReady,
      }}
    >
      {children}
    </ReportDetailContext.Provider>
  );
};

export function useReportTimer() {
  const props = useContext(ReportDetailContext) as ReportDetailProviderType;

  return {
    startTimer: props?.startTimer,
    resetTimer: props?.resetTimer,
    seconds: props?.seconds,
    minutes: props?.minutes,
    pauseTimer: props?.pauseTimer,
    isRunningTimer: props?.isRunningTimer,
  };
}

export function useReportDetail() {
  const props = useContext(ReportDetailContext) as ReportDetailProviderType;

  return {
    loadProfiles: props?.loadProfiles,
    loadProfilesConfirm: props?.loadProfilesConfirm,
    loadMoreProfilesConfirm: props?.loadMoreProfilesConfirm,
    hasMoreProfilesConfirm: props?.hasMoreProfilesConfirm,
    isLoadingMoreProfilesConfirm: props?.isLoadingMoreProfilesConfirm,
    profileList: props?.profileList,
    isLoadingProfiles: props?.isLoadingProfiles,
    isSuccessProfiles: props?.isSuccessProfiles,
    profileListConfirm: props?.profileListConfirm,
    isLoadingProfilesConfirm: props?.isLoadingProfilesConfirm,
    isSuccessProfilesConfirm: props?.isSuccessProfilesConfirm,
    report: props?.report,
    errorLoadingReport: props?.errorLoadingReport,
    isReportForbidden: props?.isReportForbidden,
    isSuccessLoadingReport: props?.isSuccessLoadingReport,
    errorLoadingProfiles: props?.errorLoadingProfiles,
    errorLoadingProfilesConfirm: props?.errorLoadingProfilesConfirm,
    blueprint: props?.blueprint,
    isSuccessLoadingBlueprintAnnotated:
      props?.isSuccessLoadingBlueprintAnnotated,
    errorBlueprint: props?.errorBlueprint,
    isLoadingBlueprint: props?.isLoadingBlueprint,
    flagCount: props?.flagCount,
    keywordCount: props?.keywordCount,
    topicCount: props?.topicCount,
    getFlags: props?.getFlags,
    getKeywordCount: props?.getKeywordCount,
    getTopicCount: props?.getTopicCount,
    flagsLoading: props?.flagsLoading,
    keywordsLoading: props?.keywordsLoading,
    topicsLoading: props?.topicsLoading,
    errorFlag: props?.errorFlag,
    errorKeyword: props?.errorKeyword,
    errorTopic: props?.errorTopic,
    filteredCountsReady: props?.filteredCountsReady,
    setFilteredCountsReady: props?.setFilteredCountsReady,
  };
}
